import { useAlerts } from '../alerts/AlertsProvider';
import {
  definitiveMutationRejection,
  inventoryMutations,
} from '../../services/inventoryMutationCoordinator';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { CollectionSummary, hasCollectionCapability } from '@qkindex/contracts';
import { Item } from '../../domain/item';
import { Location } from '../../domain/location';
import { createOperationId } from '../../domain/operationId';
import { invalidateInventory } from '../collection/inventoryInvalidation';
import { recordKey } from '../collection/multiCollectionIdentity';
import { useFeedback } from '../feedback/FeedbackProvider';
import {
  BulkLocationResult,
  LocationRequest,
  locationMoveError,
  planLocationGroups,
  retryLocationRequest,
  settleLocationResult,
} from './locationOperations';
import {
  definitiveLocationRejection,
  executeLocationRequest,
  readLocationMove,
} from './locationOperationService';

interface Context {
  userId: string;
  collections: CollectionSummary[];
  restricted: Set<string>;
  locations: Location[];
  onViewCollection?: (collectionId: string) => void;
}
type State =
  | 'queued'
  | 'submitting'
  | 'uncertain'
  | 'processing'
  | 'complete'
  | 'failed'
  | 'unsubmitted';
interface Operation extends BulkLocationResult {
  // A group survives definite-failure retries; each new attempt gets a new request ID.
  id: string;
  userId: string;
  sourceCollectionId: string;
  items: Item[];
  destination: Location;
  request: LocationRequest;
  state: State;
  busy: boolean;
  everUncertain: boolean;
  destinationItemIds: Set<string>;
}
function createSession() {
  const operations = new Map<string, Operation>();
  const listeners = new Set<() => void>();
  let snapshot: Operation[] = [];
  return {
    operations,
    recover: async (_operation: Operation): Promise<void> => undefined,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    publish: () => {
      snapshot = [...operations.values()].map((operation) => ({
        ...operation,
      }));
      listeners.forEach((listener) => listener());
    },
  };
}
// Retain terminal failures as well as pending requests across workspace remounts.
// Nothing is persisted across a browser reload or shared between accounts.
export const locationOperationSessions = new Map<
  string,
  ReturnType<typeof createSession>
>();
const emptyResult = (): BulkLocationResult => ({
  completed: [],
  pending: [],
  failed: [],
  unsubmitted: [],
});
const requestKeys = (request: LocationRequest) =>
  request.kind === 'update'
    ? request.items.map((item) =>
        recordKey({ collectionId: request.collectionId, id: item.id }),
      )
    : request.itemIds.map((id) =>
        recordKey({ collectionId: request.sourceCollectionId, id }),
      );
const authorityKey = (
  context: Context,
  operation: Operation,
  statusRead = false,
) =>
  JSON.stringify([
    context.userId,
    ...[operation.sourceCollectionId, operation.destination.collectionId].map(
      (id) => {
        const collection = context.collections.find(
          (entry) => entry.collectionId === id,
        );
        return collection
          ? [
              id,
              collection.role,
              collection.membershipVersion,
              collection.lifecycle,
              collection.lifecycleVersion,
              !statusRead && context.restricted.has(id!),
            ]
          : null;
      },
    ),
  ]);

/** Browser-owned coordination. Only admitted server moves survive closing the page. */
export function useLocationOperations(context: Context) {
  const { notify } = useFeedback();
  const alerts = useAlerts();
  const current = useRef(context);
  current.current = context;
  const session = useMemo(() => {
    if (!locationOperationSessions.has(context.userId))
      locationOperationSessions.set(context.userId, createSession());
    return locationOperationSessions.get(context.userId)!;
  }, [context.userId]);
  const active = { current: session.operations };
  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    const tracked = session.operations;
    tracked.forEach((operation) =>
      inventoryMutations.resume(operation.id, () => session.recover(operation)),
    );
    return () => {
      mounted.current = false;
      tracked.forEach((operation) => {
        if (operation.state === 'queued') {
          inventoryMutations.release(operation.id);
          tracked.delete(operation.id);
        } else {
          if (operation.state === 'submitting') {
            operation.state = 'uncertain';
            operation.everUncertain = true;
          }
          inventoryMutations.markUncertain(operation.id);
        }
      });
      session.publish();
    };
  }, [session]);
  const publish = session.publish;
  const live = (operation: Operation) =>
    mounted.current &&
    current.current.userId === operation.userId &&
    active.current.get(operation.id) === operation;
  const validation = (
    items: Item[],
    destination: Location | undefined,
    requireLocation = true,
  ) => {
    const latest = current.current;
    return (
      locationMoveError(
        items,
        destination,
        latest.collections,
        latest.restricted,
      ) ||
      (requireLocation &&
      destination &&
      !latest.locations.some(
        (entry) => recordKey(entry) === recordKey(destination),
      )
        ? 'Destination unavailable.'
        : undefined)
    );
  };
  const readsStatus = (operation: Operation) =>
    operation.request.kind === 'move' &&
    (operation.state === 'processing' ||
      (operation.state === 'uncertain' &&
        Boolean(validation(operation.items, operation.destination, false))));
  const operationError = (operation: Operation) => {
    if (!readsStatus(operation))
      return validation(
        operation.items,
        operation.destination,
        operation.state === 'queued' || operation.state === 'failed',
      );
    // Match the status endpoint: source administration and destination membership,
    // without requiring billing clearance, destination editing or a loaded location.
    const latest = current.current;
    const source = latest.collections.find(
      (entry) => entry.collectionId === operation.sourceCollectionId,
    );
    const destination = latest.collections.find(
      (entry) => entry.collectionId === operation.destination.collectionId,
    );
    if (
      !source ||
      !destination ||
      !hasCollectionCapability(source.role, 'moveItemsOut') ||
      ![source, destination].every((entry) => entry.lifecycle === 'active')
    ) {
      return 'Move status requires source administrator access and destination membership.';
    }
  };
  const overlaps = (items: Item[]) =>
    inventoryMutations.conflict(
      current.current.userId,
      items.map((item) => ({
        collectionId: item.collectionId!,
        itemIds: [item.id],
      })),
    ) ||
    [...active.current.values()].some(
      (operation) =>
        operation.userId === current.current.userId &&
        ['queued', 'submitting', 'uncertain', 'processing'].includes(
          operation.state,
        ) &&
        requestKeys(operation.request).some(
          (key) =>
            !operation.completed.includes(key) &&
            items.some((item) => recordKey(item) === key),
        ),
    );
  const error = (items: Item[], destination?: Location) =>
    validation(items, destination) ||
    (destination &&
    inventoryMutations.conflict(current.current.userId, [
      { collectionId: destination.collectionId!, itemIds: [] },
    ])
      ? 'The destination has an update awaiting confirmation.'
      : undefined) ||
    (overlaps(items)
      ? 'These items have an update awaiting confirmation. Resolve it before starting another move.'
      : undefined);
  const announce = (result: BulkLocationResult, destination: Location) => {
    // Pending and failed groups already have live queue entries. Unsubmitted
    // work has no running controller, so retain a notice explaining that outcome.
    if (!result.unsubmitted.length) return;
    alerts?.notice(current.current.userId, {
      id: `unsubmitted-location:${destination.collectionId}:${destination.id}`,
      title: 'Some location updates were not submitted',
      severity: 'warning',
      message: `${result.completed.length} updated, ${result.pending.length} awaiting confirmation, ${result.failed.length} failed, ${result.unsubmitted.length} not submitted. Check the affected items before starting a new update.`,
    });
  };
  const run = async (operation: Operation, background = false) => {
    if (!live(operation) || operation.busy) return;
    const initial = operation.state === 'queued';
    // Recovery replays the admitted payload; it does not depend on loaded inventory pages.
    if (operationError(operation)) {
      if (initial) {
        operation.state = 'unsubmitted';
        active.current.delete(operation.id);
        inventoryMutations.release(operation.id);
        publish();
      }
      return;
    }
    const statusRead = readsStatus(operation);
    const fence = authorityKey(current.current, operation, statusRead);
    const wasProcessing = operation.state === 'processing';
    operation.busy = true;
    if (initial) operation.state = 'submitting';
    operation.pending = requestKeys(operation.request).filter(
      (key) =>
        !operation.completed.includes(key) && !operation.failed.includes(key),
    );
    operation.unsubmitted = [];
    publish();
    try {
      const outcome =
        statusRead && operation.request.kind === 'move'
          ? await readLocationMove(operation.request)
          : await executeLocationRequest(operation.request);
      if (!live(operation)) return;
      if (fence !== authorityKey(current.current, operation, statusRead)) {
        operation.state = wasProcessing ? 'processing' : 'uncertain';
        operation.everUncertain = true;
        return;
      }
      if (!outcome) return;
      const newlyCompleted = outcome.completed.filter(
        (key) => !operation.completed.includes(key),
      );
      Object.assign(operation, settleLocationResult(operation, outcome));
      if (newlyCompleted.length) {
        if (operation.request.kind === 'update') {
          invalidateInventory({
            userId: operation.userId,
            collectionId: operation.sourceCollectionId,
            resources: ['items'],
          });
        } else {
          const destinationItemIds = (outcome.destinationItemIds || []).filter(
            (id) => !operation.destinationItemIds.has(id),
          );
          destinationItemIds.forEach((id) =>
            operation.destinationItemIds.add(id),
          );
          invalidateInventory({
            userId: operation.userId,
            collectionId: operation.sourceCollectionId,
            resources: ['items'],
            deletedItemIds: operation.items
              .filter((item) => newlyCompleted.includes(recordKey(item)))
              .map((item) => item.id),
          });
          invalidateInventory({
            userId: operation.userId,
            collectionId: operation.destination.collectionId!,
            resources: ['items'],
            reloadItemIds: destinationItemIds,
          });
        }
      }
      if (operation.state === 'complete' || operation.state === 'failed')
        inventoryMutations.release(operation.id);
      if (operation.state === 'complete') active.current.delete(operation.id);
      if (background && operation.state !== 'processing')
        announce(operation, operation.destination);
    } catch (cause) {
      if (!live(operation)) return;
      if (
        initial &&
        !operation.everUncertain &&
        fence === authorityKey(current.current, operation) &&
        (operation.request.kind === 'update'
          ? definitiveMutationRejection(cause)
          : definitiveLocationRejection(cause))
      ) {
        operation.state = 'failed';
        operation.failed = requestKeys(operation.request);
        operation.pending = [];
        inventoryMutations.release(operation.id);
      } else {
        // A later rejection cannot prove that an earlier lost response never committed.
        operation.everUncertain = true;
        operation.state = wasProcessing ? 'processing' : 'uncertain';
      }
    } finally {
      operation.busy = false;
      publish();
    }
  };
  const recover = async (operation: Operation) => {
    if (live(operation)) return run(operation, true);
    if (operation.busy) return;
    operation.busy = true;
    try {
      const outcome =
        operation.state === 'processing' && operation.request.kind === 'move'
          ? await readLocationMove(operation.request)
          : await executeLocationRequest(operation.request);
      if (!outcome) return;
      Object.assign(operation, settleLocationResult(operation, outcome));
      for (const collectionId of new Set([
        operation.sourceCollectionId,
        operation.destination.collectionId!,
      ])) {
        invalidateInventory({
          userId: operation.userId,
          collectionId,
          resources: ['items'],
        });
      }
      if (!outcome.pending.length) inventoryMutations.release(operation.id);
      if (operation.state === 'complete')
        session.operations.delete(operation.id);
    } finally {
      operation.busy = false;
      publish();
    }
  };
  session.recover = recover;
  const reserve = (operation: Operation) =>
    inventoryMutations.acquire({
      id: operation.id,
      userId: operation.userId,
      label: 'Update item locations',
      busy: false,
      uncertain: false,
      scopes: [
        {
          collectionId: operation.sourceCollectionId,
          itemIds:
            operation.request.kind === 'move'
              ? operation.request.itemIds
              : operation.request.items.map((item) => item.id),
        },
        { collectionId: operation.destination.collectionId!, itemIds: [] },
      ],
      retry: () => session.recover(operation),
    });
  const start = async (
    items: Item[],
    destination: Location,
    destinationTagIds?: string[],
  ): Promise<BulkLocationResult> => {
    const userId = context.userId;
    const result = emptyResult();
    const problem = error(items, destination);
    if (!mounted.current || current.current.userId !== userId || problem) {
      result.unsubmitted = items.map(recordKey);
      if (mounted.current && current.current.userId === userId && problem)
        notify(problem, 'warning');
      return result;
    }
    // A new user intent explicitly supersedes matching terminal failures, never pending work.
    for (const operation of active.current.values()) {
      if (operation.state !== 'failed') continue;
      operation.failed = operation.failed.filter(
        (key) => !items.some((item) => recordKey(item) === key),
      );
      if (!operation.failed.length) active.current.delete(operation.id);
    }
    const groups = planLocationGroups(items, destination);
    const operations = groups.flatMap((group) => {
      result.completed.push(...group.unchanged);
      if (!group.request) return [];
      const operationId = createOperationId();
      const request = {
        ...group.request,
        operationId,
        ...(group.request.kind === 'move' && destinationTagIds !== undefined
          ? { destinationTagIds }
          : {}),
      } as LocationRequest;
      const operation: Operation = {
        ...emptyResult(),
        id: operationId,
        userId,
        sourceCollectionId: group.sourceCollectionId,
        items: group.items,
        destination: group.destination,
        request,
        state: 'queued',
        busy: false,
        everUncertain: false,
        destinationItemIds: new Set(),
        unsubmitted: requestKeys(request),
      };
      reserve(operation);
      active.current.set(operation.id, operation);
      return [operation];
    });
    const fences = operations.map((operation) =>
      authorityKey(current.current, operation),
    );
    publish();
    for (const operation of operations) {
      if (live(operation)) await run(operation);
      (['completed', 'pending', 'failed', 'unsubmitted'] as const).forEach(
        (key) => result[key].push(...operation[key]),
      );
    }
    if (
      mounted.current &&
      current.current.userId === userId &&
      operations.every(
        (operation, index) =>
          authorityKey(current.current, operation) === fences[index],
      )
    )
      announce(result, destination);
    return result;
  };
  const retry = async (operationId: string) => {
    const operation = [...active.current.values()].find(
      (entry) => entry.request.operationId === operationId,
    );
    if (
      !operation ||
      !live(operation) ||
      operation.busy ||
      operationError(operation)
    )
      return;
    if (operation.state === 'failed') {
      const previous = operation.request;
      operation.request = retryLocationRequest(
        operation.request,
        operation.failed,
        createOperationId(),
      );
      try {
        reserve(operation);
      } catch {
        operation.request = previous;
        return;
      }
      operation.failed = [];
      operation.state = 'queued';
      operation.everUncertain = false;
      operation.unsubmitted = requestKeys(operation.request);
    }
    await run(operation, true);
  };
  const poll = useRef<() => Promise<void>>(async () => undefined);
  poll.current = async () => {
    for (const operation of [...active.current.values()]) {
      if (['processing', 'uncertain'].includes(operation.state))
        await run(operation, true);
    }
  };
  const hasWork = snapshot.some((operation) =>
    ['processing', 'uncertain'].includes(operation.state),
  );
  useEffect(() => {
    if (!hasWork) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      await poll.current();
      if (!cancelled)
        timer = setTimeout(() => {
          void tick();
        }, 5000);
    };
    timer = setTimeout(() => {
      void tick();
    }, 5000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hasWork, context.userId]);
  const operations = snapshot
    .filter(
      (operation) =>
        operation.userId === context.userId &&
        context.collections.some(
          (collection) =>
            collection.collectionId === operation.sourceCollectionId &&
            hasCollectionCapability(
              collection.role,
              operation.request.kind === 'move'
                ? 'moveItemsOut'
                : 'editInventory',
            ),
        ),
    )
    .map((operation) => ({
      ...operation,
      operationId: operation.request.operationId,
      blockedReason: operationError(operation),
    }));
  return { start, retry, operations, error };
}
